// ============================================
// AdjustmentHistoryPage
// Lịch sử điều chỉnh tồn kho (sheet 2 — mục #26, #29)
// ============================================
//
// Nguồn: GET /inventory/adjustments/ (API_REFERENCE "Inventory Endpoints").
// Quyền theo document: AI_ADMIN, STORE_ADMIN — KHÔNG có OPERATOR, nên route
// gác bằng INVENTORY_ADJUST (chỉ hai vai đó có trong ROLE_PERMISSIONS).
//
// Mọi biến động tồn đều đọng lại ở đây, kể cả bút toán do hệ thống sinh
// (RESTOCK khi đóng phiếu nhập, RETURN khi khách trả) — đó là điểm khác với
// modal điều chỉnh, nơi chỉ cho chọn các loại thao tác tay.
//
// Bộ lọc loại vẫn giữ đủ 7 giá trị kể cả MANUAL_CORRECTION: màn này ĐỌC lịch
// sử nên phải hiện được cả bản ghi cũ, khác với modal tạo mới.

import { useMemo, useState } from 'react';
import { ADJUSTMENT_TYPE_OPTIONS } from '@conai/shared-constants';
import type { InventoryAdjustment, StockAdjustmentType } from '@conai/shared-types';
import { useI18n } from '../../i18n';
import {
  PageContainer,
  PageHeader,
  SelectDropdown,
  DataTable,
  Pagination,
  ExportButton,
  type Column,
} from '../../components/shared';
import { useInventoryAdjustments, useExport } from '../../hooks';
import { useStoreSelectionStore } from '../../stores';
import { extractList } from '../../utils/apiList';

const PAGE_SIZE = 20;

/** Màu badge theo loại — lấy từ shared-constants để đồng bộ với chỗ khác. */
const TYPE_COLORS: Record<string, string> = {
  green: 'bg-emerald-50 text-emerald-700',
  blue: 'bg-blue-50 text-blue-700',
  orange: 'bg-orange-50 text-orange-700',
  red: 'bg-red-50 text-red-700',
  purple: 'bg-purple-50 text-purple-700',
};

/**
/**
 * Tên field của bản ghi điều chỉnh (bug #241).
 *
 * BE đã đồng bộ list và detail (18/09/2026): cả hai giờ trả `adjustment_type`,
 * `quantity_change`, `quantity_before`, `quantity_after`,
 * `adjusted_by {id, full_name}`, `loss_reason_display` — đây là bộ tên CHUẨN,
 * đọc trước tiên.
 *
 * Bộ tên cũ (`type`, `quantity`, `previous_stock`, `resulting_stock`,
 * `created_by_name`) BE vẫn giữ, không breaking.
 *
 * ⚠️ CHƯA GỠ ĐƯỢC nhánh dự phòng: bản BE này còn nằm ở nhánh local, chưa lên
 * QA. QA hiện tại vẫn CHỈ có bộ tên cũ, nên đọc thẳng tên chuẩn là ba cột rỗng
 * trắng trở lại đúng như bug #241 ban đầu.
 * Sau khi BE lên QA và smoke test xong: xoá ba helper dưới đây, đọc thẳng
 * `row.adjustment_type`, `row.quantity_change`, `row.adjusted_by?.full_name`,
 * và bỏ luôn các field alias khỏi `AdjustmentRow`.
 */
type AdjustmentRow = InventoryAdjustment & {
  /** Alias cũ của `adjustment_type` — xoá sau khi BE lên QA. */
  type?: string | null;
  /**
   * Nhãn loại từ BE.
   *
   * ⚠️ KHÔNG được dịch (BE đính chính 18/09/2026): nhãn enum bên BE là chuỗi
   * thuần, không qua `gettext`, nên field này LUÔN trả tiếng Anh bất kể
   * `Accept-Language`. Vì vậy nó chỉ là nhánh DỰ PHÒNG — nguồn chính là map
   * i18n của FE theo mã ổn định. Xem cột `adjustment_type` bên dưới.
   */
  type_display?: string | null;
  /** Alias cũ của `adjusted_by.full_name` — xoá sau khi BE lên QA. */
  created_by_name?: string | null;
};

/** Loại điều chỉnh. Tên chuẩn trước, alias cũ sau. */
function readAdjustmentType(row: AdjustmentRow): string | null {
  return row.adjustment_type ?? row.type ?? null;
}

/**
 * Chênh lệch CÓ DẤU. `quantity_change` trước, rồi `quantity`, cuối cùng mới
 * tự trừ từ tồn trước/sau.
 *
 * Kiểm `!= null` chứ không dùng `||`: một điều chỉnh có chênh lệch bằng 0 là
 * hợp lệ (kiểm đếm khớp sổ), mà `0 || x` sẽ nhảy sang nhánh sau và hiện sai.
 */
function readQuantityChange(row: AdjustmentRow): number | null {
  if (row.quantity_change != null) return row.quantity_change;
  if (row.quantity != null) return row.quantity;
  const before = row.quantity_before ?? row.previous_stock;
  const after = row.quantity_after ?? row.resulting_stock;
  if (before != null && after != null) return after - before;
  return null;
}

function readStockBefore(row: AdjustmentRow): number | null {
  return row.quantity_before ?? row.previous_stock ?? null;
}

function readStockAfter(row: AdjustmentRow): number | null {
  return row.quantity_after ?? row.resulting_stock ?? null;
}

/**
 * Nhãn nguyên nhân hao hụt, dịch theo MÃ (bug #240).
 *
 * `loss_reason_display` cũng không qua `gettext` — cùng lý do với
 * `type_display` — nên chỉ dùng làm dự phòng cho mã lạ.
 */
const LOSS_REASON_KEYS: Record<string, string> = {
  THEFT: 'lossReasonTheft',
  EXPIRED: 'lossReasonExpired',
  DAMAGED: 'lossReasonDamaged',
  OTHER: 'lossReasonOther',
};

/** Người thực hiện. `adjusted_by` là tên chuẩn; `created_by_name` là alias cũ. */
function readAdjustedBy(row: AdjustmentRow): string | null {
  return row.adjusted_by?.full_name || row.created_by_name || null;
}

function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function AdjustmentHistoryPage() {
  const { t } = useI18n();
  const selectedStoreId = useStoreSelectionStore((s) => s.selectedStoreId);

  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, error } = useInventoryAdjustments({
    page: currentPage,
    page_size: PAGE_SIZE,
    store: selectedStoreId || undefined,
    adjustment_type:
      typeFilter === 'all' ? undefined : (typeFilter as StockAdjustmentType),
  });

  // Xuất Excel/PDF (sheet 2 mục #74) — cùng cơ chế với các trang khác:
  // thêm ?export=xlsx|pdf vào chính endpoint danh sách, KÈM bộ lọc đang xem
  // để file khớp với những gì trên màn hình.
  //
  // Tên tham số `store` là CỐ Ý và đã được BE nhận (03/09/2026): endpoint này
  // trước đó chỉ nhận `store_id`, lệch với /inventory/ vốn nhận cả hai — mà
  // django-filter BỎ QUA IM LẶNG tên nó không biết, nên bộ lọc cửa hàng ở đây
  // đã không chạy suốt thời gian dài mà không ai thấy: admin một cửa hàng
  // không nhận ra vì queryset đã bị thu hẹp sẵn theo scope. Đừng "sửa" về
  // `store_id` cho giống tài liệu cũ — `store` mới là spelling nhất quán
  // trong hệ thống, và giờ cả hai đều đúng.
  const { triggerExport, isExporting } = useExport({
    endpoint: '/inventory/adjustments/',
    params: {
      store: selectedStoreId || undefined,
      adjustment_type: typeFilter === 'all' ? undefined : typeFilter,
    },
    filePrefix: 'inventory_adjustments',
  });

  // Envelope của BE không nhất quán (có meta thì {results}, không thì mảng trần)
  // — luôn đi qua extractList, đừng đọc thẳng .results.
  const rows = useMemo(() => extractList<AdjustmentRow>(data), [data]);
  const totalItems =
    (data as { count?: number; meta?: { count?: number } } | undefined)?.count ??
    (data as { meta?: { count?: number } } | undefined)?.meta?.count ??
    rows.length;
  const totalPages = Math.max(Math.ceil(totalItems / PAGE_SIZE), 1);

  const columns: Column<AdjustmentRow>[] = [
    {
      key: 'created_at',
      header: t('date'),
      width: '150px',
      render: (row) => (
        <p className="text-xs font-medium text-slate-600">{formatDateTime(row.created_at)}</p>
      ),
    },
    {
      key: 'adjustment_type',
      header: t('adjustTypeLabel'),
      width: '130px',
      render: (row) => {
        const rawType = readAdjustmentType(row);
        const option = ADJUSTMENT_TYPE_OPTIONS.find((o) => o.value === rawType);
        const cls = TYPE_COLORS[option?.color ?? ''] ?? 'bg-slate-100 text-slate-700';
        if (!rawType) return <span className="text-sm text-slate-400">—</span>;
        return (
          <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold ${cls}`}>
            {/* Thứ tự ưu tiên nhãn (bug #240) — ĐÃ ĐẢO so với bản trước:
                1. `t(option.labelKey)` — map i18n của FE theo MÃ ổn định.
                2. `type_display` — chỉ là dự phòng cho mã lạ FE chưa biết.
                3. mã thô.

                Bản trước đặt `type_display` lên đầu vì tưởng BE dịch sẵn. BE
                đính chính 18/09/2026: nhãn enum bên BE là chuỗi thuần, KHÔNG
                qua `gettext`, nên `type_display` luôn tiếng Anh bất kể
                `Accept-Language`. Để nó đi trước thì giao diện PL/UK hiện nhãn
                tiếng Anh — vẫn là bug #240, chỉ đổi ngôn ngữ sai.

                Tuyệt đối không dùng `option.label`: chuỗi tiếng Việt viết cứng
                trong shared-constants. */}
            {option ? t(option.labelKey) : row.type_display || rawType}
          </span>
        );
      },
    },
    {
      key: 'quantity_change',
      header: t('quantity'),
      width: '140px',
      render: (row) => {
        const change = readQuantityChange(row);
        const before = readStockBefore(row);
        const after = readStockAfter(row);
        if (change == null) return <span className="text-sm text-slate-400">—</span>;
        const isNegative = change < 0;
        return (
          <div>
            <p className={`text-sm font-bold ${isNegative ? 'text-red-600' : 'text-emerald-600'}`}>
              {change > 0 ? `+${change}` : change}
            </p>
            {before != null && after != null && (
              <p className="text-[10px] text-slate-400">
                {before} → {after}
              </p>
            )}
          </div>
        );
      },
    },
    {
      key: 'reason',
      header: t('reason'),
      render: (row) => (
        <p className="text-sm text-slate-700">{row.reason || '—'}</p>
      ),
    },
    {
      key: 'loss_reason',
      header: t('lossReasonLabel'),
      width: '110px',
      /**
       * Mục #220. `null`/thiếu là bình thường — phần lớn dòng trong lịch sử
       * (bán hàng, kiểm kê, RESTOCK) không thuộc diện phải khai nguyên nhân.
       * "—" ở đây nghĩa là "không áp dụng", không phải "thiếu dữ liệu".
       */
      render: (row) => {
        const key = row.loss_reason ? LOSS_REASON_KEYS[row.loss_reason] : null;
        return (
          <p className="text-sm text-slate-700">
            {key ? t(key) : row.loss_reason_display || row.loss_reason || '—'}
          </p>
        );
      },
    },
    {
      key: 'adjusted_by',
      header: t('adjustedBy'),
      width: '160px',
      render: (row) => (
        <p className="text-sm font-medium text-slate-700">
          {readAdjustedBy(row) ?? '—'}
        </p>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title={t('adjustmentHistoryTitle')}
        subtitle={t('adjustmentHistorySubtitle')}
        actions={<ExportButton onExport={triggerExport} isLoading={isExporting} />}
      />

      {error && (
        <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          {error instanceof Error ? error.message : t('adjustmentHistoryLoadError')}
        </div>
      )}

      {/* Filter */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white px-4 py-3">
        <SelectDropdown
          options={[
            { value: 'all', label: t('allTypes') },
            // `t(o.labelKey)` chứ không phải `o.label` (bug #240).
            ...ADJUSTMENT_TYPE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
          ]}
          value={typeFilter}
          onChange={(val) => {
            setTypeFilter(val);
            setCurrentPage(1);
          }}
          placeholder={t('allTypes')}
          className="w-full sm:w-[200px]"
        />
      </div>

      {/* Table */}
      <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <DataTable
          columns={columns}
          data={rows}
          rowKey="id"
          isLoading={isLoading}
          showFilter={false}
          showHeaderMenu={false}
          emptyMessage={t('adjustmentHistoryEmpty')}
          className="border-0 shadow-none"
        />
        {!isLoading && totalItems > 0 && (
          <div className="border-t border-slate-100">
            <Pagination
              variant="numbered"
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={PAGE_SIZE}
              onPageChange={setCurrentPage}
              itemLabel="adjustments"
            />
          </div>
        )}
      </div>
    </PageContainer>
  );
}
